import type { Timestamp } from "@firebase/firestore-types";
import { z } from "zod";
import type { CategoryId } from "../categories.js";
import type { TrackedCategorySplit } from "../trackedCategory/models.js";

/** GoCardless Bank Account Data sandbox institution (demo only). */
export const GCL_SANDBOX_INSTITUTION_ID = "SANDBOXFINANCE_SFIN0000";

export const InstitutionSchema = z.object({
  id: z.string(),
  name: z.string(),
  countries: z.array(z.string()),
  logo: z.string(),
});
export type Institution = z.infer<typeof InstitutionSchema>;

export const RequisitionStatusSchema = z.enum([
  "CR", // CREATED: Requisition has been successfully created, stage 1
  "GC", // GOCARDLESS: GoCardless processing failed, stage 2
  "UA", // UNDERGOING_AUTHENTICATION: End-user is redirected to the institution for authentication, stage 3
  "RJ", // REJECTED: End-user rejected authorization, stage 4
  "SA", // SELECTING_ACCOUNTS: Account selection has been finished, stage 5
  "GA", // GRANTING_ACCESS: Consent has been granted, GoCardless is processing account data, stage 6
  "LN", // LINKED: Initial connection to the institution has been finished, stage 7
  "EX", // EXPIRED: Access has expired, stage 8
  "ID", // IDENTIFIED: End-user has been identified
  "ER", // ERROR: Authorization failed
  "SU", // SUSPENDED: Access is temporary suspended
]);

export const RequisitionSchema = z.object({
  id: z.string(),
  status: RequisitionStatusSchema,
  institution_id: z.string(),
  accounts: z.array(z.string()),
  link: z.string(),
});
export const RequisitionLooseSchema = z.looseObject(RequisitionSchema.shape);
export type Requisition = z.infer<typeof RequisitionSchema>;

export const AccountMetadataStatusSchema = z.enum([
  "DISCOVERED",
  "ERROR",
  "EXPIRED",
  "PROCESSING",
  "READY",
  "SUSPENDED",
]);
export const AccountMetadataSchema = z.object({
  id: z.string(),
  iban: z.string(),
  institution_id: z.string(),
  name: z.string().optional(),
  status: AccountMetadataStatusSchema,
});
export const AccountMetadataLooseSchema = z.looseObject(AccountMetadataSchema.shape);
export type AccountMetadata = z.infer<typeof AccountMetadataSchema>;

export const AccountDetailsSchema = z.object({
  account: z.object({
    currency: z.string(),
  }),
});
export const AccountDetailsLooseSchema = z.looseObject({
  ...AccountDetailsSchema.shape,
  account: z.looseObject(AccountDetailsSchema.shape.account.shape),
});
export type AccountDetails = z.infer<typeof AccountDetailsSchema>;

export const AccountBalanceTypeSchema = z.enum([
  "closingAvailable",
  "closingBooked",
  "closingCleared",
  "expected",
  "forwardAvailable",
  "interimAvailable",
  "interimCleared",
  "information",
  "interimBooked",
  "nonInvoiced",
  "openingBooked",
  "openingAvailable",
  "openingCleared",
  "previouslyClosedBooked",
]);
export const AccountBalanceSchema = z.object({
  balances: z.array(
    z.object({
      balanceAmount: z.object({
        amount: z.string(),
        currency: z.string(),
      }),
      balanceType: AccountBalanceTypeSchema,
    }),
  ),
});
export const AccountBalanceLooseSchema = z.looseObject({
  ...AccountBalanceSchema.shape,
  balances: z.array(
    z.looseObject({
      ...AccountBalanceSchema.shape.balances.element.shape,
      balanceAmount: z.looseObject(
        AccountBalanceSchema.shape.balances.element.shape.balanceAmount.shape,
      ),
    }),
  ),
});
export type AccountBalance = z.infer<typeof AccountBalanceSchema>;

export type GclAccount = {
  balances?: AccountBalance["balances"];
  details?: AccountDetails["account"];
  metadata: AccountMetadata;
} & {
  lastTransactionsSyncedAt?: Timestamp;
  pendingTransactions: PendingTransaction[] | undefined;
};

export const BookedTransactionSchema = z.object({
  bookingDate: z.string().optional(),
  transactionAmount: z.object({
    amount: z.string(),
    currency: z.string(),
  }),
  creditorName: z.string().optional(),
  debtorName: z.string().optional(),
  remittanceInformationUnstructured: z.string().optional(),
  remittanceInformationStructured: z.string().optional(),
  internalTransactionId: z.string(),
});
export const BookedTransactionLooseSchema = z.looseObject({
  ...BookedTransactionSchema.shape,
  transactionAmount: z.looseObject(BookedTransactionSchema.shape.transactionAmount.shape),
});
export type BookedTransaction = z.infer<typeof BookedTransactionSchema>;

export type GclTransaction = BookedTransaction & {
  categoryId?: CategoryId;
  trackedCategorySplits?: TrackedCategorySplit[];
  /** AI suggestion for a tracked category; present means pending user accept/reject. */
  suggestedTrackedCategoryId?: string;
  /** Last AI suggestion the user rejected for this transaction. */
  rejectedSuggestedTrackedCategoryId?: string;
  is_flagged?: boolean;
};

export const PendingTransactionSchema = z.object({
  transactionAmount: z.object({
    amount: z.string(),
    currency: z.string(),
  }),
});
export const PendingTransactionLooseSchema = z.looseObject({
  ...PendingTransactionSchema.shape,
  transactionAmount: z.looseObject(PendingTransactionSchema.shape.transactionAmount.shape),
});
export type PendingTransaction = z.infer<typeof PendingTransactionSchema>;

export const AccountTransactionsSchema = z.object({
  transactions: z.object({
    booked: z.array(BookedTransactionSchema),
    pending: z.array(PendingTransactionSchema).optional(),
  }),
  last_updated: z.iso.datetime(),
});
export const AccountTransactionsLooseSchema = z.looseObject({
  ...AccountTransactionsSchema.shape,
  transactions: z.looseObject({
    booked: z.array(BookedTransactionLooseSchema),
    pending: z.array(PendingTransactionLooseSchema).optional(),
  }),
});
export type AccountTransactions = z.infer<typeof AccountTransactionsSchema>;
